import { backendApi } from "@/services/backend/api";
import { providesList } from "@/services/backend/_lib/tag-helpers";
import {
  Calculation,
  CreateFromOldRequest,
  CreateRequest,
  DeleteRequest,
  List,
  ListRequest,
  LoadRequirementRequest,
  PutServiceDocRequest,
  RentingCost,
  RentingPosition,
  ServiceDocumentation,
  ShowRequest,
  UpdateAdditionalCostPerYearRequest,
  UpdateAdditionalTimePerMeasureRequest,
  UpdateContainerAdditionalsRequest,
  UpdateMaintenanceLaborPriceReq,
  UpdateMaterialAdditionPriceRequest,
  UpdateMaterialPriceRequest,
  UpdateMonitorSnapshotBasePriceReq,
  UpdateMonitorSnapshotConsumableReq,
  UpdateMonitorSnapshotTimesReq,
  UpdateRentingCostDurationRequest,
  UpdateRentingCostPositionRequest,
  UpdateServiceTimePerMonitorRequest,
  UpdateServiceTimePerMonitorResponse,
  UpdateTargetRevenueRequest,
  UpdateTargetRevenueResponse,
  UpdateTravelingDurationRequest,
  UpdateTravelingDurationResponse,
} from "@/services/backend/qpm/calculations/types/calculation";

const qpmCalculationApi = backendApi
  .enhanceEndpoints({
    addTagTypes: [
      "QpmCalculation",
      "QpmCalculationMaterialPrices",
      "QpmCalculationMaintenancePrices",
      "QpmCalculationDocumentation",
      "QpmCalculationServiceDocumentation",
      "QpmCalculationServiceGroup",
      "QpmCalculationRentingCost",
      "QpmCalculationRentingCostPosition",
    ],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      qpmCalculationCreate: build.mutation<Calculation, CreateRequest>({
        query: (request) => ({
          url: `/qpm/calculation`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: ["QpmCalculation", "QpmCalculationServiceGroup"],
      }),
      qpmCalculationCreateFromOld: build.mutation<
        Calculation,
        CreateFromOldRequest
      >({
        query: (request) => ({
          url: `/qpm/calculation/create-from-old`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: ["QpmCalculation", "QpmCalculationServiceGroup"],
      }),
      qpmCalculationLoadRequirement: build.mutation<
        Calculation,
        LoadRequirementRequest
      >({
        query: (request) => ({
          url: `/qpm/calculation/load-requirement`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: [
          "QpmCalculation",
          "QpmCalculationMaterialPrices",
          "QpmCalculationMaintenancePrices",
          "QpmCalculationServiceGroup",
        ],
      }),
      qpmCalculationShow: build.query<Calculation, ShowRequest>({
        query: (request) => ({
          url: `/qpm/calculation`,
          method: "GET",
          params: request,
        }),
        providesTags: (result) =>
          result ? [{ type: "QpmCalculation", id: result.id }] : [],
      }),
      qpmCalculationGetLinkedRequirement: build.query<Calculation, ShowRequest>(
        {
          query: (request) => ({
            url: `/qpm/calculation`,
            method: "GET",
            params: request,
          }),
          providesTags: (result) =>
            result ? [{ type: "QpmCalculation", id: result.id }] : [],
        },
      ),
      qpmCalculationList: build.query<List, Partial<ListRequest>>({
        query: (request) => ({
          url: `/qpm/calculation/list`,
          method: "GET",
          params: {
            request: btoa(JSON.stringify(request)),
          },
        }),
        providesTags: (result) => providesList(result?.items, "QpmCalculation"),
      }),
      qpmCalculationDelete: build.mutation<null, DeleteRequest>({
        query: (request) => ({
          url: `/qpm/calculation`,
          method: "DELETE",
          body: request,
        }),
        invalidatesTags: () => [{ type: "QpmCalculation", id: "LIST" }],
      }),
      qpmCalculationUpdateMaterialPrice: build.mutation<
        Calculation,
        UpdateMaterialPriceRequest
      >({
        query: (request) => ({
          url: `/qpm/calculation/material-price`,
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: [
          { type: "QpmCalculationMaterialPrices", id: "LIST" },
          { type: "QpmCalculationMaintenancePrices", id: "LIST" },
          "QpmCalculation",
          "QpmCalculationMaterialPrices",
          "QpmCalculationServiceGroup",
          "QpmCalculationMaintenancePrices",
        ],
      }),
      qpmCalculationUpdateMaterialAdditionPrice: build.mutation<
        Calculation,
        UpdateMaterialAdditionPriceRequest
      >({
        query: (request) => ({
          url: `/qpm/calculation/material-addition-price`,
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: [
          { type: "QpmCalculationMaterialPrices", id: "LIST" },
          { type: "QpmCalculationMaintenancePrices", id: "LIST" },
          "QpmCalculation",
          "QpmCalculationMaterialPrices",
          "QpmCalculationServiceGroup",
          "QpmCalculationMaintenancePrices",
        ],
      }),
      qpmCalcServiceDocumentationPut: build.mutation<
        ServiceDocumentation,
        PutServiceDocRequest
      >({
        query: (request) => ({
          url: `/qpm/calculation/service-documentation`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: [
          "QpmCalculationServiceDocumentation",
          "QpmCalculation",
          "QpmCalculationServiceGroup",
          "QpmCalculationRentingCost",
        ],
      }),
      qpmCalcRentingCostDurationPut: build.mutation<
        RentingCost,
        UpdateRentingCostDurationRequest
      >({
        query: (request) => ({
          url: `/qpm/calculation/renting-cost/update-duration`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: [
          "QpmCalculation",
          "QpmCalculationRentingCost",
          "QpmCalculationRentingCostPosition",
        ],
      }),
      qpmCalcRentingCostPositionPricePost: build.mutation<
        RentingPosition,
        UpdateRentingCostPositionRequest
      >({
        query: (request) => ({
          url: `/qpm/calculation/renting-cost/update-position-price`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: [
          { type: "QpmCalculationMaterialPrices", id: "LIST" },
          { type: "QpmCalculationMaintenancePrices", id: "LIST" },
          "QpmCalculation",
          "QpmCalculationMaterialPrices",
          "QpmCalculationServiceGroup",
          "QpmCalculationMaintenancePrices",
          "QpmCalculationRentingCost",
          "QpmCalculationRentingCostPosition",
        ],
      }),
      qpmCalcServiceGroupServiceTimePerMonitorPost: build.mutation<
        UpdateServiceTimePerMonitorResponse,
        UpdateServiceTimePerMonitorRequest
      >({
        query: (request) => ({
          url: `/qpm/calculation/service-cost/update-service-time-per-monitor`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: (result) =>
          result
            ? [
                { type: "QpmCalculationMaterialPrices", id: "LIST" },
                { type: "QpmCalculationMaintenancePrices", id: "LIST" },
                { type: "QpmCalculation", id: result.calculationId },
                "QpmCalculationMaterialPrices",
                "QpmCalculation",
                "QpmCalculationServiceGroup",
                "QpmCalculationMaintenancePrices",
                "QpmCalculationRentingCost",
                "QpmCalculationRentingCostPosition",
              ]
            : [],
      }),
      qpmCalcServiceGroupTargetRevenuePost: build.mutation<
        UpdateTargetRevenueResponse,
        UpdateTargetRevenueRequest
      >({
        query: (request) => ({
          url: `/qpm/calculation/service-cost/update-target-revenue`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: (result) =>
          result
            ? [
                { type: "QpmCalculationMaterialPrices", id: "LIST" },
                { type: "QpmCalculationMaintenancePrices", id: "LIST" },
                { type: "QpmCalculation", id: result.calculationId },
                "QpmCalculationMaterialPrices",
                "QpmCalculation",
                "QpmCalculationServiceGroup",
                "QpmCalculationMaintenancePrices",
                "QpmCalculationRentingCost",
                "QpmCalculationRentingCostPosition",
              ]
            : [],
      }),
      qpmCalcServiceGroupTravelingDurationPost: build.mutation<
        UpdateTravelingDurationResponse,
        UpdateTravelingDurationRequest
      >({
        query: (request) => ({
          url: `/qpm/calculation/service-cost/update-traveling-duration`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: (result) =>
          result
            ? [
                { type: "QpmCalculationMaterialPrices", id: "LIST" },
                { type: "QpmCalculationMaintenancePrices", id: "LIST" },
                { type: "QpmCalculation", id: result.calculationId },
                "QpmCalculationMaterialPrices",
                "QpmCalculation",
                "QpmCalculationServiceGroup",
                "QpmCalculationMaintenancePrices",
                "QpmCalculationRentingCost",
                "QpmCalculationRentingCostPosition",
              ]
            : [],
      }),
      qpmCalcServiceGroupAdditionalTimePerMeasurePost: build.mutation<
        Calculation,
        UpdateAdditionalTimePerMeasureRequest
      >({
        query: (request) => ({
          url: `/qpm/calculation/service-cost/update-additional-time-per-measure`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: [
          "QpmCalculation",
          "QpmCalculationServiceGroup",
          "QpmCalculationRentingCost",
        ],
      }),
      qpmCalcServiceGroupAdditionalCostPerYearPost: build.mutation<
        Calculation,
        UpdateAdditionalCostPerYearRequest
      >({
        query: (request) => ({
          url: `/qpm/calculation/service-cost/update-additional-cost-per-year`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: [
          "QpmCalculation",
          "QpmCalculationServiceGroup",
          "QpmCalculationRentingCost",
        ],
      }),
      qpmCalcServiceGroupContainerAdditionalsPost: build.mutation<
        Calculation,
        UpdateContainerAdditionalsRequest
      >({
        query: (request) => ({
          url: `/qpm/calculation/service-cost/update-container-additionals`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: [
          "QpmCalculation",
          "QpmCalculationServiceGroup",
          "QpmCalculationRentingCost",
        ],
      }),
      qpmCalcUpdateMonitorSnapshotBasePrice: build.mutation<
        Calculation,
        UpdateMonitorSnapshotBasePriceReq
      >({
        query: (request) => ({
          url: `/qpm/calculation/monitor-snapshot/base-price`,
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (result) =>
          result ? [{ type: "QpmCalculation", id: result.id }] : [],
      }),
      qpmCalcUpdateMonitorSnapshotTimes: build.mutation<
        Calculation,
        UpdateMonitorSnapshotTimesReq
      >({
        query: (request) => ({
          url: `/qpm/calculation/monitor-snapshot/times`,
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (result) =>
          result ? [{ type: "QpmCalculation", id: result.id }] : [],
      }),
      qpmCalcUpdateMonitorSnapshotConsumable: build.mutation<
        Calculation,
        UpdateMonitorSnapshotConsumableReq
      >({
        query: (request) => ({
          url: `/qpm/calculation/monitor-snapshot/consumable`,
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (result) =>
          result ? [{ type: "QpmCalculation", id: result.id }] : [],
      }),
      qpmCalcUpdateMaintenanceMeasureRate: build.mutation<
        Calculation,
        UpdateMaintenanceLaborPriceReq
      >({
        query: (request) => ({
          url: `/qpm/calculation/maintenance/labor-price`,
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (result) =>
          result ? [{ type: "QpmCalculation", id: result.id }] : [],
      }),
    }),
  });

export const {
  useQpmCalculationCreateMutation,
  useQpmCalculationCreateFromOldMutation,
  useQpmCalculationLoadRequirementMutation,
  useQpmCalculationShowQuery,
  useQpmCalculationListQuery,
  useQpmCalculationDeleteMutation,
  useQpmCalculationUpdateMaterialAdditionPriceMutation,
  useQpmCalculationUpdateMaterialPriceMutation,
  useQpmCalcServiceDocumentationPutMutation,
  useQpmCalcRentingCostDurationPutMutation,
  useQpmCalcRentingCostPositionPricePostMutation,
  useQpmCalcServiceGroupServiceTimePerMonitorPostMutation,
  useQpmCalcServiceGroupTargetRevenuePostMutation,
  useQpmCalcServiceGroupTravelingDurationPostMutation,
  useQpmCalcServiceGroupAdditionalTimePerMeasurePostMutation,
  useQpmCalcServiceGroupAdditionalCostPerYearPostMutation,
  useQpmCalcServiceGroupContainerAdditionalsPostMutation,
  useQpmCalcUpdateMonitorSnapshotBasePriceMutation,
  useQpmCalcUpdateMonitorSnapshotTimesMutation,
  useQpmCalcUpdateMonitorSnapshotConsumableMutation,
  useQpmCalcUpdateMaintenanceMeasureRateMutation,
} = qpmCalculationApi;
